/**
 * The client-side data store — one copy of the read model in the browser.
 *
 * Every page reads through this instead of fetching for itself, and a background prefetch fills
 * it on sign-in so a page is already answered before it is opened (`prefetch.ts`). Three
 * properties make that safe rather than merely fast, and each is a rule that would look like an
 * over-complication to anyone who had not hit the failure it prevents:
 *
 * 1. **Entries carry their snapshot id, and a new one wipes the rest.** The wire envelope has
 *    carried `snapshotId` all along. A promoted snapshot changes every figure in the product at
 *    once, so an entry fetched before the promotion and one fetched after it must never be read
 *    together — a KPI strip from Tuesday beside a table from Wednesday is two true answers making
 *    one false screen. There is no partial reconciliation here: the moment a response names a
 *    different snapshot, everything else goes.
 *
 * 2. **Entries carry their owner.** The store persists to `localStorage`, which outlives a
 *    sign-out on a shared machine. `open()` compares the signed-in address against the stored one
 *    and wipes on any difference, and sign-out wipes outright. Nobody inherits the previous user's
 *    book of business because they used the same browser.
 *
 * 3. **Invalidation is a generation counter, not a pass over every entry.** *Refresh* has to make
 *    ~60 entries stale at once; rewriting all of them would mean ~60 `localStorage` writes and a
 *    visible stall. Bumping one number does it, and an entry written under an older generation is
 *    stale by definition.
 *
 * ## Freshness
 *
 * `fresh` for six hours (`TTL_MS`), then `stale`. Stale is **not** empty: a stale entry still
 * paints immediately and revalidates behind the page, because the alternative — a skeleton over
 * data we already have — is a worse answer to "what does the book look like". Only a `missing`
 * entry produces a loading state.
 *
 * Six hours against a snapshot that turns over once a day is deliberately loose: it exists to
 * bound how long a wrong-but-plausible copy can survive a promotion that the snapshot guard
 * somehow missed, not to chase changes that are not happening.
 *
 * **A descriptor may override both** (spec 007, research R-06). `ttlMs` replaces the six hours
 * for that key and is persisted on the entry, so a rehydrated copy keeps it — the Audit page's
 * responses use the owner's 12 hours. `staleWhen(value, fetchedAt, now)` marks an entry stale however
 * young it is; only the Audit List uses it, to fetch again once a newer run has become due
 * (AU-19, 07:30 New York time on the next weekday). A function cannot be persisted, so the caller holding the descriptor
 * passes it to `freshness()`. Every other query is unaffected.
 */

const STORAGE_PREFIX = 'bob.data.v1:';
const META_KEY = `${STORAGE_PREFIX}__meta`;

/** Six hours, per the owner's requirement (2026-08-28). */
export const TTL_MS = 6 * 60 * 60 * 1000;

/**
 * Entries above this stay in memory for the session but are never written to disk.
 *
 * `localStorage` gives roughly 5 MB for the whole origin. One Delivery scope carries 21 metric
 * columns across every filter combination for two dozen managers, so a handful of the largest
 * payloads could consume the quota and evict everything else — trading sixty instant pages for
 * three. A payload that will not fit is better served from memory and refetched next session.
 */
const MAX_PERSISTED_CHARS = 400_000;

export type Freshness = 'fresh' | 'stale' | 'missing';

export interface QueryDescriptor<T> {
  /** Stable, canonical identity for this request. Built in `queries.ts`, never by a caller. */
  key: string;
  fetch: () => Promise<T>;
  /** Human-readable, for the Settings progress readout. */
  label?: string;
  /** Replaces `TTL_MS` for this key. Persisted on the entry. */
  ttlMs?: number;
  /**
   * True ⇒ the entry is stale regardless of its age (the Audit List's due-time refresh, AU-19).
   * It is handed the entry's `fetchedAt` too, because "has a newer run become due SINCE this copy
   * was fetched" is a question about the copy, not only its contents.
   * Method syntax on purpose: a function-typed property would make the descriptor contravariant
   * in `T`, and every `QueryDescriptor<unknown>` list (the prefetch manifest) would stop compiling.
   */
  staleWhen?(value: T, fetchedAt: number, now: number): boolean;
}

export interface StoreEntry<T = unknown> {
  value: T;
  fetchedAt: number;
  snapshotId: string | null;
  generation: number;
  /** The descriptor's `ttlMs` when it set one; absent ⇒ `TTL_MS`. */
  ttlMs?: number;
}

export interface StoreStats {
  entries: number;
  persistedEntries: number;
  bytes: number;
  oldestFetchedAt: number | null;
  newestFetchedAt: number | null;
  snapshotId: string | null;
  generation: number;
  persistAvailable: boolean;
}

interface StoredMeta {
  owner: string | null;
  snapshotId: string | null;
  generation: number;
}

/** `localStorage` throws rather than returning null in some privacy modes. */
function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * The snapshot a payload belongs to, or `null` for one that is not snapshot-scoped.
 *
 * Structural rather than typed against `Envelope<T>`: the store holds envelopes *and* the
 * directory listing, and a payload that carries no snapshot identity is simply not subject to
 * rule 1 above. Reading the field where it exists is what makes the guard automatic — no caller
 * has to remember to declare it.
 */
function snapshotIdOf(value: unknown): string | null {
  if (value == null || typeof value !== 'object') return null;
  const candidate = (value as { snapshotId?: unknown }).snapshotId;
  return typeof candidate === 'string' && candidate.length > 0 ? candidate : null;
}

export class DataStore {
  private memory = new Map<string, StoreEntry>();
  /**
   * Keys known to be absent from `localStorage`.
   *
   * Without it, every render and every prefetch notification re-reads and re-parses storage for
   * a key that is not there — and a prefetch run notifies fifty times while several hooks are
   * mounted. Cleared per key on write, and wholesale on clear.
   */
  private absent = new Set<string>();
  private inflight = new Map<string, Promise<unknown>>();
  private listeners = new Set<() => void>();

  private owner: string | null = null;
  private snapshotId: string | null = null;
  private generation = 1;
  private persistAvailable = true;

  /**
   * Foreground fetches in flight. The prefetcher waits while this is above zero, so filling the
   * store never competes for a connection with the page someone is actually looking at.
   */
  private foreground = 0;

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  /**
   * Bind the store to a signed-in address and adopt whatever that address left behind.
   *
   * A different owner — or unreadable meta — wipes rather than merges. There is no case where
   * mixing two accounts' cached snapshots is preferable to one cold start.
   */
  open(owner: string): void {
    this.owner = owner;

    const meta = this.readMeta();
    if (!meta || meta.owner !== owner) {
      this.clear();
      this.writeMeta();
      return;
    }

    this.snapshotId = meta.snapshotId;
    this.generation = meta.generation;
    this.notify();
  }

  /** Sign-out. The persisted copy goes with the session that made it. */
  close(): void {
    this.owner = null;
    this.clear();
  }

  // -------------------------------------------------------------------------
  // Reading
  // -------------------------------------------------------------------------

  /**
   * The entry for a key, hydrated from `localStorage` on first touch.
   *
   * Lazy on purpose: parsing sixty payloads at start-up would stall the first paint for no
   * benefit, since a session typically reads a handful of them. The returned object is immutable
   * and replaced wholesale on write, which is what lets `useSyncExternalStore` compare identities.
   */
  entry<T>(key: string): StoreEntry<T> | undefined {
    const held = this.memory.get(key);
    if (held) return held as StoreEntry<T>;
    if (this.absent.has(key)) return undefined;

    const hydrated = this.hydrate<T>(key);
    if (hydrated) this.memory.set(key, hydrated);
    else this.absent.add(key);
    return hydrated;
  }

  get<T>(key: string): T | null {
    return (this.entry<T>(key)?.value as T | undefined) ?? null;
  }

  freshness<T = unknown>(
    key: string,
    now = Date.now(),
    staleWhen?: (value: T, fetchedAt: number, now: number) => boolean,
  ): Freshness {
    const entry = this.entry<T>(key);
    if (!entry) return 'missing';
    if (entry.generation !== this.generation) return 'stale';
    if (staleWhen && staleWhen(entry.value, entry.fetchedAt, now)) return 'stale';
    return now - entry.fetchedAt < (entry.ttlMs ?? TTL_MS) ? 'fresh' : 'stale';
  }

  isFetching(key: string): boolean {
    return this.inflight.has(key);
  }

  // -------------------------------------------------------------------------
  // Fetching
  // -------------------------------------------------------------------------

  /**
   * Resolve a descriptor, from the store where possible and from the network where not.
   *
   * `background: true` marks a prefetch or a revalidation — it does not count toward the
   * foreground gate, so the prefetcher cannot deadlock waiting on itself.
   *
   * Concurrent callers for one key share a single request. Without that, a page mounting three
   * components that need the same payload would issue three identical requests, and the
   * prefetcher would race every one of them.
   */
  async run<T>(
    descriptor: QueryDescriptor<T>,
    options: { force?: boolean; background?: boolean } = {},
  ): Promise<T> {
    const { key, fetch } = descriptor;
    const { force = false, background = false } = options;

    if (!force && this.freshness(key, Date.now(), descriptor.staleWhen) === 'fresh') {
      return this.get<T>(key) as T;
    }

    const existing = this.inflight.get(key);
    if (existing) return existing as Promise<T>;

    if (!background) this.foreground += 1;

    const request = fetch()
      .then((value) => {
        this.write(key, value, descriptor.ttlMs);
        return value;
      })
      .finally(() => {
        this.inflight.delete(key);
        if (!background) this.foreground = Math.max(0, this.foreground - 1);
        this.notify();
      });

    this.inflight.set(key, request as Promise<unknown>);
    // Announce the in-flight state so a hook can show a loading indicator for a missing entry.
    this.notify();

    return request;
  }

  /** True while any page-driven request is outstanding. The prefetcher's yield signal. */
  get foregroundBusy(): boolean {
    return this.foreground > 0;
  }

  // -------------------------------------------------------------------------
  // Writing and invalidation
  // -------------------------------------------------------------------------

  private write(key: string, value: unknown, ttlMs?: number): void {
    const responseSnapshot = snapshotIdOf(value);

    // Rule 1. A response naming a different snapshot means every other entry describes a
    // retired one. Dropping them is not a cache optimisation — it is what stops two snapshots
    // being read as one.
    if (responseSnapshot && this.snapshotId && responseSnapshot !== this.snapshotId) {
      this.clear();
      this.snapshotId = responseSnapshot;
    } else if (responseSnapshot && !this.snapshotId) {
      this.snapshotId = responseSnapshot;
    }

    const entry: StoreEntry = {
      value,
      fetchedAt: Date.now(),
      snapshotId: responseSnapshot,
      generation: this.generation,
      ...(ttlMs !== undefined ? { ttlMs } : {}),
    };

    this.memory.set(key, entry);
    this.absent.delete(key);
    this.persist(key, entry);
    this.writeMeta();
  }

  /**
   * Make everything stale without discarding it — the *Refresh* button.
   *
   * Values are kept so pages keep painting while the refetch runs. A refresh that blanked every
   * screen first would look like a fault, and the numbers it replaces them with are usually the
   * same ones.
   */
  invalidateAll(): void {
    this.generation += 1;
    this.writeMeta();
    this.notify();
  }

  /** Drop everything, memory and disk. */
  clear(): void {
    this.memory.clear();
    this.absent.clear();
    this.snapshotId = null;

    const store = storage();
    if (store) {
      for (const key of this.storedKeys(store)) store.removeItem(key);
      try {
        store.removeItem(META_KEY);
      } catch {
        // Nothing to do: an unreadable store is also an unwritable one.
      }
    }

    this.notify();
  }

  // -------------------------------------------------------------------------
  // Subscription
  // -------------------------------------------------------------------------

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private notify(): void {
    for (const listener of this.listeners) listener();
  }

  // -------------------------------------------------------------------------
  // Stats — for the Settings readout
  // -------------------------------------------------------------------------

  stats(): StoreStats {
    const store = storage();
    let bytes = 0;
    let persistedEntries = 0;

    if (store) {
      for (const key of this.storedKeys(store)) {
        persistedEntries += 1;
        bytes += (store.getItem(key)?.length ?? 0) * 2; // UTF-16 code units
      }
    }

    const times = [...this.memory.values()].map((e) => e.fetchedAt);

    return {
      entries: this.memory.size,
      persistedEntries,
      bytes,
      oldestFetchedAt: times.length ? Math.min(...times) : null,
      newestFetchedAt: times.length ? Math.max(...times) : null,
      snapshotId: this.snapshotId,
      generation: this.generation,
      persistAvailable: this.persistAvailable,
    };
  }

  // -------------------------------------------------------------------------
  // Persistence
  // -------------------------------------------------------------------------

  private storedKeys(store: Storage): string[] {
    const keys: string[] = [];
    try {
      for (let i = 0; i < store.length; i += 1) {
        const key = store.key(i);
        if (key && key.startsWith(STORAGE_PREFIX) && key !== META_KEY) keys.push(key);
      }
    } catch {
      return keys;
    }
    return keys;
  }

  private hydrate<T>(key: string): StoreEntry<T> | undefined {
    const store = storage();
    if (!store) return undefined;

    try {
      const raw = store.getItem(STORAGE_PREFIX + key);
      if (!raw) return undefined;

      const parsed = JSON.parse(raw) as Partial<StoreEntry<T>>;
      if (typeof parsed.fetchedAt !== 'number' || typeof parsed.generation !== 'number') {
        return undefined;
      }

      // An entry from a snapshot the store has since moved past is not merely stale — it is
      // wrong, and must not be painted even for the moment before a revalidation lands.
      if (this.snapshotId && parsed.snapshotId && parsed.snapshotId !== this.snapshotId) {
        store.removeItem(STORAGE_PREFIX + key);
        return undefined;
      }

      return {
        value: parsed.value as T,
        fetchedAt: parsed.fetchedAt,
        snapshotId: parsed.snapshotId ?? null,
        generation: parsed.generation,
        ...(typeof parsed.ttlMs === 'number' ? { ttlMs: parsed.ttlMs } : {}),
      };
    } catch {
      // Unparseable is handled exactly like absent.
      return undefined;
    }
  }

  private persist(key: string, entry: StoreEntry): void {
    const store = storage();
    if (!store || !this.persistAvailable) return;

    let serialized: string;
    try {
      serialized = JSON.stringify(entry);
    } catch {
      return; // Not serialisable — memory only.
    }

    if (serialized.length > MAX_PERSISTED_CHARS) return;

    try {
      store.setItem(STORAGE_PREFIX + key, serialized);
      return;
    } catch {
      // Quota. Make room from the oldest end and try once more.
    }

    this.evictOldest(store);

    try {
      store.setItem(STORAGE_PREFIX + key, serialized);
    } catch {
      // Still no room. Stop writing for this session rather than thrashing the quota on every
      // response; memory keeps serving, and the next session starts with a clean budget.
      this.persistAvailable = false;
    }
  }

  /** Oldest quarter by fetch time, so eviction costs the least useful entries first. */
  private evictOldest(store: Storage): void {
    const dated = this.storedKeys(store)
      .map((key) => {
        try {
          const parsed = JSON.parse(store.getItem(key) ?? '{}') as { fetchedAt?: number };
          return { key, at: parsed.fetchedAt ?? 0 };
        } catch {
          return { key, at: 0 };
        }
      })
      .sort((a, b) => a.at - b.at);

    const drop = Math.max(1, Math.ceil(dated.length / 4));
    for (const { key } of dated.slice(0, drop)) {
      try {
        store.removeItem(key);
      } catch {
        return;
      }
    }
  }

  private readMeta(): StoredMeta | null {
    const store = storage();
    if (!store) return null;

    try {
      const raw = store.getItem(META_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<StoredMeta>;
      if (typeof parsed.generation !== 'number') return null;
      return {
        owner: parsed.owner ?? null,
        snapshotId: parsed.snapshotId ?? null,
        generation: parsed.generation,
      };
    } catch {
      return null;
    }
  }

  private writeMeta(): void {
    const store = storage();
    if (!store) return;

    try {
      store.setItem(
        META_KEY,
        JSON.stringify({
          owner: this.owner,
          snapshotId: this.snapshotId,
          generation: this.generation,
        } satisfies StoredMeta),
      );
    } catch {
      this.persistAvailable = false;
    }
  }
}

/**
 * One store per document. Exported as a singleton because the prefetcher, the provider, and
 * every hook must be looking at the same copy — two stores would mean two prefetches and two
 * answers to the same question.
 */
export const dataStore = new DataStore();
